import type { ComponentType } from "react";
import { UserIcon } from "@heroicons/react/24/outline";
import trpc from "../../utils/trpc.js";
import { FillContainerLoader } from "../../components/loader.js";
import { Garrla1Layout } from "./templates/garrla-1.js";
import { Nestle1Layout } from "./templates/nestle-1.js";
import { Unilever1Layout } from "./templates/unilever-1.js";
import type { TrpcOutput } from "../../utils/trpc.js";

type ProfileResult = NonNullable<TrpcOutput["profile"]["getProfile"]>;

type TemplateLayouts = {
  [K in ProfileResult["template"]]: ComponentType<{
    data: Extract<ProfileResult, { template: K }>["data"];
  }>;
};

// Registry maps template key → layout component.
const templateLayouts: TemplateLayouts = {
  "garrla-1": Garrla1Layout,
  "nestle-1": Nestle1Layout,
  "unilever-1": Unilever1Layout,
};

export function Component() {
  const { data: profileData, isLoading } = trpc.profile.getProfile.useQuery(undefined, { staleTime: Infinity });

  if (isLoading) {
    return (
      <div className="h-56">
        <FillContainerLoader delayMs={0} size="large" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full border border-gray bg-white/60 shadow-xl shadow-lac-leman/10">
          <UserIcon className="h-8 w-8 text-lac-leman" />
        </div>
        <h2 className="mb-2 text-lg font-semibold">Profile not yet available</h2>
        <p className="text-gray-500 max-w-sm text-sm">
          Your profile has not been configured. Please check back later or contact your instructor for further guidance.
        </p>
      </div>
    );
  }

  const TemplateLayout = templateLayouts[profileData.template] as ComponentType<{ data: ProfileResult["data"] }>;
  return <TemplateLayout data={profileData.data} />;
}
